
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    
import style from '../styles/form.module.css'

import { getLayout, LayoutContext } from '../components/sg/Layout'
import Form, { Step } from '../components/Form'
import useTranslation from 'next-translate/useTranslation'
import classNames from 'classnames/bind'
import resolveProductUnit from '../helpers/resolveProductUnit'
import { useState, useContext, useLayoutEffect, useEffect, useRef } from 'react'
import Router, { useRouter } from 'next/router'
import PaymentMethods from '../components/PaymentMethods'
import PayNow from '../components/PayNow'
import Stripe, { getCreditCardFees } from '../components/Stripe'
import useLocalStorageState from 'use-local-storage-state'
import { useLivePrice, currencies, defaultCurrency } from '../hooks/usePrices'
import useTimer from '../hooks/useTimer'
import useCopy from '../hooks/useCopy'
import usePrevious from '../hooks/usePrevious'
import { event } from '../helpers/analytics'
import useAuth from '../hooks/useAuth'
import { useLiveHoldings, products } from '../hooks/useHoldings'
import useUser from '../hooks/useUser'
import Link from 'next/link'
import BankTransfer from '../components/BankTransfer'

let token

export default function Buy () {
  const instructionFormElem = useRef()
  const transactionFormElem = useRef()
  const priceElem = useRef()
  const amountElem = useRef()
  const { t } = useTranslation('main')
  const router = useRouter()
  const { locale } = router
  const c = classNames.bind(style)
  // eslint-disable-next-line no-unused-vars
  const [layoutContext, setLayoutContext] = useContext(LayoutContext)
  const handleCopy = useCopy()
  const { authedFetch } = useAuth()
  const { user, account } = useUser()
  const [currentStep, setCurrentStep] = useState('instruction')
  const [successMsg, setSuccessMsg] = useState()

  const [transactionType, setTransactionType] = useState('buy')
  const [transaction, setTransaction] = useLocalStorageState('transaction_v2', {
    defaultValue: {
      type: transactionType,
      product: 'AuGrams',
      currency: account?.PreferredCurrency || defaultCurrency
    }
  })

  const productUnit = 'gram'
  const { holdings, balances: { Cash: { Amount: cashBalance } } } = useLiveHoldings(transaction?.currency)
  const { productUnitLabel, productUnitShortLabel, productPriceResolution, productUnitFractionDigits } = resolveProductUnit(productUnit, 1)
  const { price: { value: spotPrice }, products: productPrices, maxTransactionAmount, maxUnfundedAmount, lockInPeriod, fx } = useLivePrice(transaction?.currency, transaction.product === 'AuGrams' ? 'gld' : 'sil')
  const previousSpotPrice = usePrevious(spotPrice)
  const { remaining, remainingString, startTimer, stopTimer } = useTimer(lockInPeriod * 1000)

  const productHolding = holdings[transaction.product]?.Amount || 0
  const lockedProductHolding = holdings[transaction.product]?.Bonus || 0
  const purchasePrice = productPrices[transaction.product]?.purchase || 0
  const sellbackPrice = productPrices[transaction.product]?.sellback || 0

  const minimumAmount = 0.01
  const minimumPrice = Math.round(purchasePrice * minimumAmount * 100) / 100

  if (typeof window !== 'undefined') {
    useLayoutEffect(() => {
      setTimeout(() => {
        if (amountElem?.current) {
          amountElem.current.focus()
          amountElem.current.select()
        }
      }, 400)
      if (window.location.pathname.includes('/sell')) {
        setTransactionType('sell')
      }
    }, [])
  }

  useEffect(() => {
    if (account?.PreferredCurrency !== transaction?.currency) {
      setTransaction(Object.assign({}, transaction, {
        currency: account?.PreferredCurrency
      }))
    }
  }, [account])

  useEffect(() => {
    if (purchasePrice > 0) {
      setTransaction(Object.assign({}, transaction, {
        premium: transactionType === 'buy' ? purchasePrice / spotPrice : sellbackPrice / spotPrice,
        basePrice: transactionType === 'buy' ? purchasePrice : sellbackPrice
      }))
    }
  }, [purchasePrice])

  const updateConversion = elem => {
    if (!elem || !amountElem || !priceElem || !spotPrice) {
      return
    }
    const value = parseFloat(elem.value.replace(/,|\//, '.'))
    const basePrice = transactionType === 'buy' ? purchasePrice : sellbackPrice
    const premium = basePrice / spotPrice
    elem.setCustomValidity('')
    if (elem.id === 'price') {
      const amount = Math[transactionType === 'buy' ? 'floor' : 'ceil'](value / basePrice * Math.pow(10, productUnitFractionDigits)) / Math.pow(10, productUnitFractionDigits) || 0
      const topup = transactionType === 'buy' ? Math.abs(Math.min(cashBalance - value, 0)) : 0
      setTransaction(Object.assign({}, transaction, {
        type: transactionType,
        price: value,
        amount,
        basePrice,
        premium,
        premiumPercent: premium / basePrice,
        topup,
        total: topup,
        methodId: undefined,
        methodType: undefined,
        percentFee: 0
      }))
      amountElem.current.value = amount.toLocaleString(locale, { useGrouping: false, minimumFractionDigits: productUnitFractionDigits, maximumFractionDigits: productUnitFractionDigits })
      amountElem.current.setCustomValidity('')
    } else {
      const price = Math.floor(value * basePrice * 100) / 100 || 0
      const topup = transactionType === 'buy' ? Math.abs(Math.min(cashBalance - price, 0)) : 0
      setTransaction(Object.assign({}, transaction, {
        type: transactionType,
        price,
        amount: value,
        basePrice,
        premium,
        premiumPercent: premium / basePrice,
        topup,
        total: topup,
        methodId: undefined,
        methodType: undefined,
        percentFee: 0
      }))
      priceElem.current.value = price.toLocaleString(locale, { useGrouping: false, minimumFractionDigits: 2, maximumFractionDigits: 2 })
      priceElem.current.setCustomValidity('')
    }
  }

  const handleInstructionSubmit = e => {
    e.preventDefault()
    setLayoutContext({})

    if (!account.HasAddress) {
      setLayoutContext({ message: t('buysell-update-profile') })
      Router.push('/settings/profile')
      return
    } else if ((user.IsExpired || !account.HasBank) && transactionType === 'buy') {
      Router.push('/upload')
      return
    }

    const amount = parseFloat(amountElem.current.value.replace(/,|\//, '.'))
    const price = parseFloat(priceElem.current.value.replace(/,|\//, '.'))
    const maxAmount = (cashBalance || 0) + (((account.IsVerified ? maxTransactionAmount : maxUnfundedAmount) || 0) / (fx || 1))

    if (!transaction?.currency || cashBalance === undefined) {
      setLayoutContext({ message: t('buysell-notavailable') })
      return
    } else if (transactionType === 'sell' && amount > (productHolding - lockedProductHolding) * productPriceResolution) {
      amountElem.current.setCustomValidity(t('buysell-amountexceedsholding', { maximum: (productHolding - lockedProductHolding).toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: productUnitFractionDigits }) + productUnitShortLabel }))
    } else if (transactionType === 'buy' && price > maxAmount) {
      priceElem.current.setCustomValidity(t('buysell-amountexceedsmaximum', { maximum: maxAmount.toLocaleString(locale, { style: 'currency', currency: transaction.currency, currencyDisplay: 'symbol', minimumFractionDigits: 0, maximumFractionDigits: 2 }) }))
    } else if (price > maxTransactionAmount / fx) {
      priceElem.current.setCustomValidity(t('buysell-amountexceedsdailymaximum', { maximum: (maxTransactionAmount / fx).toLocaleString(locale, { style: 'currency', currency: transaction.currency, currencyDisplay: 'symbol', minimumFractionDigits: 0, maximumFractionDigits: 2 }) }))
    } else if (amount < minimumAmount) {
      amountElem.current.setCustomValidity(t('buysell-amounttoolow', { amount: minimumAmount.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: productUnitFractionDigits }) + productUnitShortLabel }))
    } else if (transactionType === 'buy' && price < minimumPrice) {
      priceElem.current.setCustomValidity(t('buysell-pricetoolow', { price: minimumPrice.toLocaleString(locale, { style: 'currency', currency: transaction.currency, currencyDisplay: 'symbol', minimumFractionDigits: 2, maximumFractionDigits: 2 }), amount: minimumAmount + productUnitShortLabel }))
    }

    if (instructionFormElem.current.reportValidity()) {
      const getQuote = async () => {
        const response = await authedFetch(`/api/gb-savings/${transactionType === 'buy' ? 'purchase' : 'sellback'}/${transaction.product}/${transaction.currency}?amt=${amount.toFixed(6)}`)
        const json = await response.json()
        if (response.status === 200) {
          const topup = json.Use === 'P' ? Math.abs(Math.min(0, json.PostBalance)) - Math.abs(Math.min(0, json.PreBalance)) : 0
          const storageFee = json.Chg?.find(c => c.ID === 1575)?.Qty || 0
          token = json.EncToken
          startTimer(json.Exp)
          setTransaction(Object.assign({}, transaction, {
            type: json.Use === 'P' ? 'buy' : 'sell',
            product: json.Product,
            currency: json.Cur,
            price: json.Total,
            amount: json.Amount + storageFee,
            basePrice: json.Use === 'P' ? json.Spot : json.UPrice,
            premium: json.Premium,
            premiumPercent: json.PremiumPct || 0,
            topup,
            total: topup,
            storageFee,
            postBalance: json.PostBalance,
            preBalance: json.PreBalance,
            postHolding: json.PostHolding + lockedProductHolding,
            preHolding: json.PreHolding + lockedProductHolding
          }, topup === 0 ? {
            methodId: undefined,
            methodType: undefined,
            percentFee: 0
          } : {}))
          setCurrentStep('quote')
        } else {
          setCurrentStep('instruction')
          if (json.maximum !== undefined) {
            setLayoutContext({
              message: transactionType === 'buy'
                ? t('buysell-maxgoldspend', { maximum: json.maximum.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: productUnitFractionDigits }) + productUnitShortLabel })
                : t('buysell-amountexceedsholding', { maximum: json.maximum.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: productUnitFractionDigits }) + productUnitShortLabel })
            })
          } else if (json.fiatLimit !== undefined) {
            setLayoutContext({ message: t('buysell-amountexceedsremainingdailymaximum', { maximum: json.fiatLimit.toLocaleString(locale, { style: 'currency', currency: transaction.currency, currencyDisplay: 'symbol', minimumFractionDigits: 2, maximumFractionDigits: 2 }) }) })
          } else {
            setLayoutContext({ message: json.detail || t('buysell-quoteerror') })
          }
        }
      }
      setCurrentStep('loadingquote')
      getQuote()
    }
  }

  useEffect(() => {
    if (currentStep === 'quote') {
      startTimer()
      if (transaction?.topup === 0) {
        transactionFormElem.current.querySelector('button').focus()
      }
    } else if (currentStep === 'instruction') {
      token = ''
    }
    return stopTimer
  }, [currentStep])

  useEffect(() => {
    if (remaining <= 0) {
      setCurrentStep('instruction')
    }
  }, [remaining])

  useEffect(() => {
    if (previousSpotPrice !== spotPrice && currentStep === 'instruction') {
      updateConversion(amountElem?.current)
    }
  }, [spotPrice])

  useEffect(() => {
    setTransaction(Object.assign(transaction, {
      type: transactionType
    }))
    updateConversion(amountElem?.current)
  }, [transactionType])

  const handleMaxSelect = () => {
    const elem = transactionType === 'buy' ? priceElem.current : amountElem.current
    const number = transactionType === 'buy' ? cashBalance : (productHolding - lockedProductHolding) * productPriceResolution
    elem.value = Math.max(number || 0, 0).toLocaleString(locale, { useGrouping: false, minimumFractionDigits: transactionType === 'buy' ? 2 : productUnitFractionDigits, maximumFractionDigits: transactionType === 'buy' ? 2 : productUnitFractionDigits })
    updateConversion(elem)
    elem.focus()
  }

  const handleSuccess = msg => {
    setCurrentStep('success')
    setSuccessMsg(typeof msg === 'string' ? msg : (transaction.type === 'buy'
      ? t('buysell-purchase-success', { amount: Math.abs(transaction.amount || 0).toLocaleString(locale, { useGrouping: false, minimumFractionDigits: 0, maximumFractionDigits: productUnitFractionDigits }) + productUnitShortLabel })
      : t('buysell-sale-success', { amount: Math.abs(transaction.price || 0).toLocaleString(locale, { style: 'currency', currency: transaction.currency, currencyDisplay: 'symbol', minimumFractionDigits: 2, maximumFractionDigits: 2 }) })
    ))
    setTimeout(transaction.methodId === 3
      ? () => {
        setTransaction({
          type: transaction.type,
          product: transaction.product,
          currency: account?.PreferredCurrency || defaultCurrency
        })
        window.location = '/portfolio'
      } : () => {
        setTransaction({
          type: transaction.type,
          product: transaction.product,
          currency: account?.PreferredCurrency || defaultCurrency
        })
        Router.push('/portfolio')
      }, 30000)
    event({ action: 'goal', category: transaction.type, label: transaction.topup > 0 ? (transaction.methodId === 3 ? 'creditcard' : 'transfer') : 'cash', value: transaction.price })
  }

  const handleQuoteSubmit = e => {
    e.preventDefault()
    setLayoutContext({})

    transactionFormElem.current.removeAttribute('novalidate')
    if (transactionFormElem.current.reportValidity()) {
      const confirm = async () => {
        const response = await authedFetch(`/api/gb-savings/${transaction.type === 'buy' ? 'purchase' : 'sellback'}/confirm`, {
          method: 'POST',
          body: JSON.stringify({
            EncToken: token
          })
        })
        if (response.status === 200) {
          const json = await response.json()
          const topup = Math.abs(json.topUpAmount)
          setTransaction(Object.assign({}, transaction, {
            topup,
            total: topup * (1 + transaction.percentFee || 1)
          }))
          if (transaction.type === 'buy' && topup > 0 && transaction.methodId > 0 && transaction.methodId < 3) {
            setCurrentStep(transaction.methodType)
          } else if (transaction.type === 'buy' && topup > 0 && transaction.methodId === 3) {
            setCurrentStep('creditcard')
          } else {
            handleSuccess()
          }
        } else {
          setCurrentStep('instruction')
          try {
            const json = await response.json()
            setLayoutContext({
              message: json.detail || t('buysell-confirmerror')
            })
          } catch (e) {
            setLayoutContext({
              message: t('buysell-confirmerror')
            })
          }
        }
      }
      setCurrentStep('loading')
      confirm()
    } else {
      setLayoutContext({
        message: t('buysell-selectpaymentmethod')
      })
    }
  }

  const handleSelectPaymentMethod = async method => {
    if (method?.id) {
      if (method.type === 'creditcard') {
        const response = await authedFetch('/api/chargescc')
        const chargesCC = await response.json()
        if (chargesCC) {
          setTransaction(Object.assign(transaction, {
            percentFee: getCreditCardFees(chargesCC, transaction.currency, transaction.topup) / transaction.topup,
            methodId: method.id,
            methodType: method.type,
            total: transaction.topup + getCreditCardFees(chargesCC, transaction.currency, transaction.topup)
          }))
        }
      } else {
        const percentFee = method.percentFeeFunc(transaction.currency)
        setTransaction(Object.assign(transaction, {
          percentFee,
          methodId: method.id,
          methodType: method.type,
          total: transaction.topup + (transaction.topup * percentFee) || 0
        }))
      }
    }
  }

  const handleError = message => {
    setLayoutContext({
      message: message || t('buysell-unknownerror')
    })
    handleCancel()
  }

  const handleTransferSubmit = () => handleSuccess(t('buysell-topupsuccess-awaitingfunds'))

  const handleCreditCardError = message => {
    if (message) {
      setCurrentStep('creditcard')
      setLayoutContext({ message })
    } else {
      setCurrentStep('quote')
      setLayoutContext({
        message: t('buysell-unavailerror')
      })
    }
  }

  const handleCreditCardSubmit = () => {
    setLayoutContext({})
    setCurrentStep('loading')
  }

  const handleCreditCardComplete = () => setTimeout(handleSuccess, 1800)

  const handleCancel = () => {
    setTransaction({
      type: transaction.type,
      product: transaction.product,
      currency: account?.PreferredCurrency || defaultCurrency
    })
    setCurrentStep('instruction')
  }

  const arrow = right => <span className={c('preicon', { right })}><svg version='1.1' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 970.477 970.477'><path d='M842.849,970.477c26.601,0,53.5-8.8,75.7-27c51.4-41.9,59.101-117.4,17.2-168.8l-234.6-288l237-290.9 c41.899-51.399,34.1-127-17.2-168.8c-51.4-41.899-127-34.1-168.8,17.2l-298.8,366.7c-36,44.1-36,107.399,0,151.6l296.4,363.9 C773.45,955.377,808.049,970.477,842.849,970.477z' /><path d='M328.85,926.276c23.7,29.101,58.301,44.2,93.101,44.2c26.6,0,53.5-8.8,75.7-27c51.399-41.9,59.1-117.4,17.199-168.8 l-234.599-288l237-290.9c41.9-51.399,34.1-127-17.2-168.8c-51.399-41.899-127-34.1-168.799,17.2l-298.9,366.7 c-36,44.1-36,107.5,0,151.6L328.85,926.276z' /></svg></span>

  const getLabels = (productCode) => {
    if (productCode === 'AuGrams') {
      return {
        'buysell-product-label': t('buysell-product-gold-label'),
        'buysell-productholding': t('buysell-productholding-gold'),
        'buysell-productholdingforsale': t('buysell-productholdingforsale-gold'),
        'buysell-buyproductpreview-title': t('buysell-buygoldpreview-title'),
        'buysell-sellproductpreview-title': t('buysell-sellgoldpreview-title')
      }
    }
    if (productCode === 'AgGrams') {
      return {
        'buysell-product-label': t('buysell-product-silver-label'),
        'buysell-productholding': t('buysell-productholding-silver'),
        'buysell-productholdingforsale': t('buysell-productholdingforsale-silver'),
        'buysell-buyproductpreview-title': t('buysell-buysilverpreview-title'),
        'buysell-sellproductpreview-title': t('buysell-sellsilverpreview-title')
      }
    }
    if (productCode === 'PtGrams') {
      return {
        'buysell-product-label': t('buysell-product-platinum-label'),
        'buysell-productholding': t('buysell-productholding-platinum'),
        'buysell-productholdingforsale': t('buysell-productholdingforsale-platinum'),
        'buysell-buyproductpreview-title': t('buysell-buyplatinumpreview-title'),
        'buysell-sellproductpreview-title': t('buysell-sellplatinumpreview-title')
      }
    }
  }

  return (
    <Form
      title={transactionType === 'buy' ? t('buysell-buy-title') : t('buysell-sell-title')}
      onClose={['instruction', 'paynow', 'bank', 'success', 'creditcard'].includes(currentStep) ? (transaction?.methodId === 3 ? () => (window.location = '/portfolio') : () => Router.push('/portfolio')) : undefined}
      onBack={['quote', 'creditcard'].includes(currentStep) ? () => setCurrentStep('instruction') : undefined}
      step={currentStep}
      tab={transactionType}
      value={transaction?.price}
      label={currentStep === 'quote' && remainingString}
    >
      <Step name='instruction'>
        <div className={c('options')}>
          <hr />
          <button className={'light ' + c({ active: transactionType === 'buy' })} onClick={() => setTransactionType('buy')} title={t('buysell-typeselect-buy-title')}>{t('buysell-typeselect-buy')}</button>
          <button className={'light ' + c({ active: transactionType === 'sell' })} onClick={() => setTransactionType('sell')} title={t('buysell-typeselect-sell-title')}>{t('buysell-typeselect-sell')}</button>
          <hr />
        </div>
        <form ref={instructionFormElem} id='instructionForm' onSubmit={handleInstructionSubmit} className={c('instruction', transactionType)}>
          <div className={c('inputfield', transaction.product.toLowerCase())}>
            <label htmlFor='amount'>{transactionType === 'buy' ? t('buysell-buyamount-label') : t('buysell-sellamount-label')} ({productUnitLabel})</label>
            {arrow(transactionType === 'buy')}
            <input type='decimal' id='amount' ref={amountElem} disabled={currentStep !== 'instruction'} defaultValue={(transaction?.amount || 0).toLocaleString(locale, { useGrouping: false, minimumFractionDigits: productUnitFractionDigits, maximumFractionDigits: productUnitFractionDigits })} required pattern={`[0-9]*([,./][0-9]{0,${productUnitFractionDigits}})?`} inputMode='decimal' placeholder={t('buysell-amount-placeholder')} onChange={() => updateConversion(amountElem.current)} />
            <select className={c('product')} onChange={e => setTransaction(Object.assign({}, transaction, { product: e.target.value }))} value={transaction?.product}>
              {products.map(c => <option key={c.code} value={c.code}>{getLabels(c.code)['buysell-product-label']}</option>)}
            </select>
            {transactionType === 'sell' && (productHolding - lockedProductHolding > 0) && (
              <button type='button' className={'light ' + c('quickselect')} onClick={() => handleMaxSelect()}>
                {t('buysell-quickselect-max')}
              </button>
            )}
            {transactionType === 'buy' && <div>{getLabels(transaction.product)['buysell-productholding']}: {productHolding !== undefined ? (productHolding * productPriceResolution).toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: productUnitFractionDigits }) + productUnitShortLabel : ''}</div>}
            {transactionType === 'sell' && <div>{getLabels(transaction.product)['buysell-productholdingforsale']}: {productHolding !== undefined ? ((productHolding - lockedProductHolding) * productPriceResolution).toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: productUnitFractionDigits }) + productUnitShortLabel : ''}</div>}
          </div>
          <div className={c('inputfield', 'fiat')}>
            <label htmlFor='price'>{transactionType === 'buy' ? t('buysell-payprice-label') : t('buysell-sellprice-label')}</label>
            {arrow(transactionType === 'sell')}
            <input type='decimal' id='price' ref={priceElem} disabled={currentStep !== 'instruction'} defaultValue={(transaction?.price || 0).toLocaleString(locale, { useGrouping: false, minimumFractionDigits: 2, maximumFractionDigits: 2 })} required pattern='[0-9]*([,./][0-9]{0,2})?' inputMode='decimal' placeholder={t('buysell-price-placeholder')} onChange={() => updateConversion(priceElem.current)} />
            <select onChange={e => setTransaction(Object.assign({}, transaction, { currency: e.target.value }))} value={transaction?.currency}>
              {currencies.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
            </select>
            {transactionType === 'buy' && cashBalance > 0 && (
              <button type='button' className={'light ' + c('quickselect')} onClick={() => handleMaxSelect()}>
                {t('buysell-quickselect-max')}
              </button>
            )}
            <div>{t('buysell-cashbalance')}: {cashBalance !== undefined && transaction?.currency ? cashBalance.toLocaleString(locale, { style: 'currency', currency: transaction.currency, currencyDisplay: 'symbol', minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''} &nbsp;{transactionType === 'buy' && <Link href='/deposit'><a>{t('buysell-fundyouraccount')}</a></Link>}</div>
          </div>
          <br />
          <button type='submit'>{transactionType === 'buy' ? t('buysell-buypreviewbutton') : t('buysell-sellpreviewbutton')}</button>
          {transactionType === 'buy' && (
            <>
              <br />
              <br />
              <br />
              <Link href='/recurring'><a>{t('buysell-setuprecurring-link')}</a></Link>
            </>
          )}
          <br />
          <br />
        </form>
      </Step>
      <Step name='loadingquote'>
        <div className={c('loading')}>
          <img src='/images/loader.svg' />
        </div>
      </Step>
      <Step name='quote'>
        <form noValidate ref={transactionFormElem} id='transactionForm' onSubmit={handleQuoteSubmit} className={c('transaction')}>
          {transaction?.postBalance !== undefined && transaction.currency && (
            <div className={c('quote') + ' ' + style[transaction.type]}>
              <h3>
                {transaction.type === 'buy'
                  ? (getLabels(transaction.product)['buysell-buyproductpreview-title'])
                  : (getLabels(transaction.product)['buysell-sellproductpreview-title'])}
              </h3>
              <div className={c('highlight')}>
                <output aria-label={t('buysell-preview-amount') + ' ' + transaction.currency}>
                  {transaction.type === 'buy' ? '+' : '-'}{(transaction.amount || 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: productUnitFractionDigits })}{productUnitShortLabel}
                </output>
              </div>
              <div className={c('lineitem')}>
                <label htmlFor='basePrice'>
                  {transaction.type === 'buy' ? t('buysell-preview-askprice') : t('buysell-preview-price')}/{productUnitLabel}
                </label>
                <output id='basePrice'>
                  {(transaction.basePrice || 0).toLocaleString(locale, { style: 'currency', currency: transaction.currency, currencyDisplay: 'symbol', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </output>
              </div>
              <div className={c('lineitem')}>
                <label htmlFor='premium'>
                  {t('buysell-preview-premium', { percent: (transaction.premiumPercent || (Math.round((transaction.premium / (transaction.basePrice || 1)) * 1000) / 1000)).toLocaleString(locale, { style: 'percent', minimumFractionDigits: 0, maximumFractionDigits: 2 }) })}/{productUnitLabel}
                </label>
                <output id='premium'>
                  {(transaction.premium || 0).toLocaleString(locale, { style: 'currency', currency: transaction.currency, currencyDisplay: 'symbol', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </output>
              </div>
              {transactionType === 'sell' && transaction.storageFee ? (
                <div className={c('lineitem')}>
                  <label htmlFor='fee'>
                    {t('buysell-preview-storagefee')}
                  </label>
                  <output id='fee'>
                    {Math.abs(transaction.storageFee || 0).toLocaleString(locale, { minimumFractionDigits: productUnitFractionDigits, maximumFractionDigits: productUnitFractionDigits })}{productUnitShortLabel}
                  </output>
                </div>
              ) : null}
              {transaction.topup > 0 && (
                <div className={c('lineitem')}>
                  <label htmlFor='fee'>
                    {t('buysell-preview-paymentmethodfee')}
                  </label>
                  <output id='fee'>
                    {transaction.percentFee
                      ? (transaction.topup * (transaction.percentFee || 0)).toLocaleString(locale, { style: 'currency', currency: transaction.currency, currencyDisplay: 'symbol', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : t('buysell-preview-paymentmethodfee-nofee')}
                  </output>
                </div>
              )}
              <div className={c('lineitem', 'total')}>
                <label>
                  {transactionType === 'buy' ? t('buysell-buypreview-total') : t('buysell-sellpreview-total')} ({transaction.currency})
                </label>
                <output>
                  {Math.abs(transaction.price + (transaction.topup * (transaction.percentFee === undefined ? 0 : transaction.percentFee)) || 0).toLocaleString(locale, { style: 'currency', currency: transaction.currency, currencyDisplay: 'symbol', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </output>
              </div>
              <div className={c('accounts')}>
                <div className={c('productamount')}>
                  {transaction.product === 'AgGrams' && (
                    <img className={c('silverbar')} src='/images/silver.png' alt={t('buysell-silverbar-alt')} />
                  )}
                  {transaction.product === 'AuGrams' && (
                    <img className={c('goldbar')} src='/images/gold.png' alt={t('buysell-goldbar-alt')} />
                  )}
                  {transaction.product === 'PtGrams' && (
                    <img className={c('silverbar')} src='/images/platinum.png' alt={t('buysell-platinumbar-alt')} />
                  )}
                  <output id='productAmountBefore' className={c('before')}>
                    {transaction.preHolding.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: productUnitFractionDigits })}{productUnitShortLabel}
                  </output>
                  <label htmlFor='productAmountBefore' className={c('label')}>
                    {t('buysell-preview-productamount-before')}
                  </label>
                  <span className={c('arrow')}>❯</span>
                  <output id='productAmountAfter' className={c('after')}>
                    {transaction.postHolding.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: productUnitFractionDigits })}{productUnitShortLabel}
                  </output>
                  <label htmlFor='productAmountAfter' className={c('label')}>
                    {t('buysell-preview-productamount-after')}
                  </label>
                </div>
                <div className={c('cashbalance')}>
                  <span className={c('dollar')}>$</span>
                  <output id='cashBalanceBefore' className={c('before')}>
                    {transaction.preBalance.toLocaleString(locale, { style: 'currency', currency: transaction.currency, currencyDisplay: 'symbol', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </output>
                  <label htmlFor='cashBalanceBefore' className={c('label')}>
                    {t('buysell-preview-cashbalance-before')}
                  </label>
                  <span className={c('arrow')}>❯</span>
                  <output id='cashBalanceAfter' className={c('after')}>
                    {Math.max(transaction.postBalance, 0).toLocaleString(locale, { style: 'currency', currency: transaction.currency, currencyDisplay: 'symbol', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </output>
                  <label htmlFor='cashBalanceAfter' className={c('label')}>
                    {t('buysell-preview-cashbalance-after')}
                  </label>
                </div>
              </div>
            </div>
          )}
          <br />
          {transaction?.topup > 0 && <PaymentMethods selected={transaction?.methodId} currency={transaction?.currency} instant onSelect={async method => await handleSelectPaymentMethod(method)} />}
          <br />
          <button className='pulsate' type='submit' disabled={!transaction?.methodId && transaction?.topup > 0}>{transaction?.type === 'buy' ? t('buysell-submitpurchasebutton') : t('buysell-submitsalebutton')}</button>
          <br />
          <br />
        </form>
      </Step>
      <Step name='paynow'>
        {transaction?.currency === 'SGD' && transaction?.topup > 0 && <PayNow active={currentStep === 'paynow'} instant amount={transaction.topup} onSubmit={handleTransferSubmit} onCancel={handleCancel} onError={handleError} onCopy={handleCopy} />}
      </Step>
      <Step name='bank'>
        {transaction?.topup > 0 && <BankTransfer locale={locale} active={currentStep === 'bank'} instant amount={transaction.topup} currency={transaction.currency} onSubmit={handleTransferSubmit} onCancel={handleCancel} onError={handleError} onCopy={handleCopy} />}
      </Step>
      <Step name='creditcard'>
        {transaction?.topup > 0 && <Stripe active={currentStep === 'creditcard'} instant currency={transaction?.currency} amount={transaction.topup} onSubmit={handleCreditCardSubmit} onComplete={handleCreditCardComplete} onError={handleCreditCardError} onCancel={handleCancel} />}
      </Step>
      <Step name='loading'>
        <div className={c('loading')}>
          <img src='/images/loader.svg' />
        </div>
      </Step>
      <Step name='success'>
        <div className={c({ success: currentStep === 'success' })} style={{ marginTop: '-8em' }}>
          <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 70 70'>
            <path fill='#D8D8D8' d='M35,60 C21.1928813,60 10,48.8071187 10,35 C10,21.1928813 21.1928813,10 35,10 C48.8071187,10 60,21.1928813 60,35 C60,48.8071187 48.8071187,60 35,60 Z M23.6332378,33.2260427 L22.3667622,34.7739573 L34.1433655,44.40936 L47.776114,27.6305926 L46.223886,26.3694074 L33.8566345,41.59064 L23.6332378,33.2260427 Z' />
            <circle stroke='#F6C358' cx='35' cy='35' r='24' strokeWidth='1.3' strokeLinecap='round' fill='transparent' />
            <polyline strong='#F6C358' strokeWidth='1.3' points='23 34 34 43 47 27' fill='transparent' />
          </svg>
        </div>
        {successMsg && (
          <div>
            <h3>{t('buysell-success-title')}<br />{successMsg}</h3>
            <span className={c('checkout-message')}>{t('buysell-checkout-message')}</span>
          </div>
        )}
      </Step>
    </Form>
  )
}

Buy.requiresAuth = true
Buy.getLayout = getLayout
Buy.requiresVerified = true


    export async function getStaticProps(ctx) {
        
        
        return {
          
          
          props: {
            
            ...(await __loadNamespaces({
              ...ctx,
              pathname: '/buy',
              loaderName: 'getStaticProps',
              ...__i18nConfig,
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
            }))
          }
        }
    }
  